import {Injectable} from '@angular/core';
import {UrlParamsService} from '../url-params.service';
import {ApplicationContext, ApplicationContextUrlParamValue} from '../../../consts/application-context.enum';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {PaymentMethod} from '../../../consts/payment-method.enum';
import {NextBannerConfig} from '../../../models/next-banners-config';
import {SalesChannel} from '../../../consts/sales-channel.enum';
import {salesChannel} from '../../../utils/utils';
import {QueryParamName} from '../../../enums/query-param-name.enum';
import {ApplicationStateService} from '../../../../shared/services/application-state.service';
import {BusinessParamName} from '../../../../shared/models/request-offer/request-offer.enum';
import {ApplicationContextsConfigBuilder} from './application-contexts-config';
import {PartnerService} from '../../../../shared/services/partner.service';

@Injectable({
  providedIn: 'root'
})
export class ApplicationContextService {

  context: string;
  private config: ApplicationContextConfig;
  private contextsConfig: ApplicationContextConfig[];

  get partnerDefined(): boolean {
    return this.partnerService.partnerDefined || this.urlParamsService.partnerDataDefined || this.stateService.partnerBusinessParamsDefined;
  }

  constructor(private urlParamsService: UrlParamsService,
              private partnerService: PartnerService,
              private stateService: ApplicationStateService) {
  }

  get contextDefined(): boolean {
    return !!this.config;
  }

  get allowedPaymentMethodsDefined(): boolean {
    return this.contextDefined && !!this.config.allowedPaymentMethods && !!this.config.allowedPaymentMethods.length;
  }

  get navigationVisible(): boolean {
    return this.contextDefined && isNotNullOrUndefined(this.config.navigationHidden) ? !this.config.navigationHidden : true;
  }

  get sendOfferVisible(): boolean {
    return this.contextDefined && isNotNullOrUndefined(this.config.sendOfferHidden) ? !this.config.sendOfferHidden : true;
  }

  get submitGoogleReviewVisible(): boolean {
    return this.contextDefined && isNotNullOrUndefined(this.config.submitGoogleReviewHidden) ? !this.config.submitGoogleReviewHidden : true;
  }

  get stepsConfigDefined(): boolean {
    return this.contextDefined && !!this.config.steps;
  }

  get isDiagnosticStationContext(): boolean {
    return this.config ? this.config.context === ApplicationContext.DIAGNOSTIC_STATION : false;
  }

  get isAgentContext(): boolean {
    return this.contextDefined ? this.config.context === ApplicationContext.AGENT : false;
  }

  get isAgentContextWithPartner(): boolean {
    return this.isAgentContext && this.partnerDefined;
  }

  get calculationEmailRequired(): boolean {
    return this.contextDefined && isNotNullOrUndefined(this.config.calculationEmailRequired) ? this.config.calculationEmailRequired : true;
  }

  get calculationPhoneNumberRequired(): boolean {
    return this.contextDefined && isNotNullOrUndefined(this.config.calculationPhoneNumberRequired) ? this.config.calculationPhoneNumberRequired : false;
  }

  get calculationPhoneNumberVisible(): boolean {
    return this.contextDefined && isNotNullOrUndefined(this.config.calculationPhoneNumberVisible) ? this.config.calculationPhoneNumberVisible : true;
  }

  get headerLogoRedirectDisabled(): boolean {
    return this.contextDefined ? this.config.headerLogoRedirectDisabled : false;
  }

  get headerTitle(): string {
    return this.contextDefined ? this.config.headerTitle : undefined;
  }

  get headerTitleBadge(): string {
    return this.contextDefined ? this.config.headerTitleBadge : undefined;
  }

  get defaultCalculationEmail(): string {
    return this.contextDefined ? this.config.defaultCalculationEmail : null;
  }

  get calculationsCacheDisabled(): boolean {
    return this.contextDefined && isNotNullOrUndefined(this.config.calculationsCacheDisabled) ? this.config.calculationsCacheDisabled : false;
  }

  init(): void {
    const configBuilder = new ApplicationContextsConfigBuilder(this.partnerDefined);
    this.contextsConfig = configBuilder.createConfig();
    const context = this.getContext();
    const config = this.getConfig(context);
    if (config) {
      if (this.isContextAvailable(config)) {
        this.context = context;
        this.config = config;
      } else {
        console.warn(`Context ${context} isn't available for sales channel ${salesChannel()}.`);
      }
    }
    this.handleContactWidget();
  }

  isStepDisabled(stepKey: string): boolean {
    if (this.stepsConfigDefined) {
      const step = this.getStepConfig(stepKey);
      return step ? step.disabled : false;
    }
    return false;
  }

  isSectionDisabled(stepKey: string, sectionKey: string): boolean {
    if (this.stepsConfigDefined) {
      const step = this.getStepConfig(stepKey);
      if (step) {
        if (step.disabled) {
          return true;
        }
        if (!!step.disabledSections) {
          return step.disabledSections.includes(sectionKey);
        }
      }
      return false;
    }
    return false;
  }

  isPaymentMethodAllowed(paymentMethod: PaymentMethod): boolean {
    if (this.allowedPaymentMethodsDefined) {
      return this.config.allowedPaymentMethods.includes(paymentMethod);
    }
    return true;
  }

  isAgreementDisabled(agreementCode: string): boolean {
    if (this.contextDefined && isNotNullOrUndefined(this.config.disabledAgreements)) {
      return this.config.disabledAgreements.includes(agreementCode);
    }
    return false;
  }

  getStepConfig(stepKey: string): ApplicationContextStepConfig {
    return this.stepsConfigDefined ? this.config.steps.find(config => config.stepKey === stepKey) : null;
  }

  getStepCustomBanner(stepKey: string): NextBannerConfig | null {
    const stepConfig = this.getStepConfig(stepKey);
    return stepConfig ? stepConfig.banner : null;
  }

  isContextAvailable(config: ApplicationContextConfig): boolean {
    return config.salesChannels.includes(<SalesChannel>salesChannel());
  }

  private getConfig(contextUrlParamValue: string): ApplicationContextConfig {
    return this.contextsConfig.find(config => config.urlParamValue === contextUrlParamValue);
  }

  private handleContactWidget(): void {
    if (this.contextDefined && this.config.thuliumWidgetHidden) {
      document.querySelector('body').classList.add(`body--contact-widget-hidden`);
    }
  }

  private getContext(): string {
    const urlContext = this.urlParamsService.getQueryParam(QueryParamName.CONTEXT);
    let context;
    if (isNotNullOrUndefined(urlContext) && urlContext !== '') {
      try {
        context = atob(urlContext);
      } catch (err) {
        context = urlContext;
      }
      return context;
    }
    return <string>this.stateService.getBusinessParameterValue(BusinessParamName.APPLICATION_CONTEXT);
  }
}

export interface ApplicationContextConfig {
  context: ApplicationContext;
  urlParamValue: ApplicationContextUrlParamValue;
  salesChannels?: SalesChannel[]; // array of sales channels in which the context is enabled & available
  navigationHidden?: boolean; // default - false
  sendOfferHidden?: boolean; // default - false
  submitGoogleReviewHidden?: boolean; // default - false
  steps?: ApplicationContextStepConfig[];
  calculationEmailRequired?: boolean;
  defaultCalculationEmail?: string; // portal's save offer fix - backup e-mail address will be used in calculation if user'll skip it
  calculationPhoneNumberRequired?: boolean;
  calculationPhoneNumberVisible?: boolean;
  allowedPaymentMethods?: PaymentMethod[];
  thuliumWidgetHidden?: boolean;
  headerLogoRedirectDisabled?: boolean;
  headerTitle?: string;
  headerTitleBadge?: string;
  disabledAgreements?: string[];
  calculationsCacheDisabled?: boolean; // default - false; if true, calculations aren't saved in cookies
}

export interface ApplicationContextStepConfig {
  stepKey: string;
  disabled?: boolean; // default - false
  disabledSections?: string[];
  buttonLabel?: string; // allows to overwrite CTA step button label
  banner?: NextBannerConfig;
}
